/**
 * <need-help-deciding> — product page question box that asks SagePilot.
 *
 * Two ways of reaching SagePilot, chosen by the section settings:
 *  - Answer API URL set: POSTs the question and product context to that URL and shows
 *    the returned answer inline, below the input.
 *  - Otherwise: sends the question through SagePilot's chat widget (window.ChatWidget,
 *    loaded in layout/theme.liquid). The widget's sendMessage() only confirms delivery;
 *    it never hands the reply text back to the page, so the reply appears in the
 *    SagePilot chat window, which we open for the customer.
 */
(function () {
  if (customElements.get('need-help-deciding')) return;

  const REQUEST_TIMEOUT_MS = 30000;
  const WIDGET_WAIT_MS = 10000;
  // Description sent with the first chat message; kept short because it shows in the transcript.
  const WIDGET_DESCRIPTION_LIMIT = 300;
  // Codes the widget returns while it is still initialising or syncing identity.
  const WIDGET_RETRY_CODES = ['not_initialized', 'identity_pending'];
  const WIDGET_MAX_RETRIES = 3;
  // Typewriter placeholder timing.
  const TYPE_DELAY_MS = 60;
  const DELETE_DELAY_MS = 30;
  const HOLD_DELAY_MS = 1500;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function truncate(text, limit) {
    return text.length > limit ? `${text.slice(0, limit).trim()}…` : text;
  }

  /**
   * Types an input's placeholder texts one at a time: types a text, holds it, deletes it,
   * then moves to the next, forever. While the input is focused or has a value it pauses
   * on the whole current text; with reduced motion it shows only the first text.
   */
  class PlaceholderTypewriter {
    constructor(input, texts) {
      this.input = input;
      // Split into code points so emoji aren't cut in half mid-type.
      this.texts = texts.map((text) => Array.from(text));
      this.timer = null;
      this.onStateChange = this.update.bind(this);
    }

    start() {
      this.stop();
      if (!this.texts.length) return;

      // The server renders the whole first text, so begin by holding it, then deleting.
      this.index = 0;
      this.length = this.texts[0].length;
      this.deleting = true;
      this.render();

      this.input.addEventListener('focus', this.onStateChange);
      this.input.addEventListener('blur', this.onStateChange);
      this.input.addEventListener('input', this.onStateChange);
      reducedMotion.addEventListener('change', this.onStateChange);
      this.update();
    }

    stop() {
      clearTimeout(this.timer);
      this.timer = null;
      this.input.removeEventListener('focus', this.onStateChange);
      this.input.removeEventListener('blur', this.onStateChange);
      this.input.removeEventListener('input', this.onStateChange);
      reducedMotion.removeEventListener('change', this.onStateChange);
    }

    // Runs or pauses the animation to match the input: it only runs while empty and blurred.
    update() {
      if (!this.texts.length) return;

      const paused = reducedMotion.matches || this.input.value !== '' || document.activeElement === this.input;
      if (!paused) {
        if (!this.timer) this.schedule(this.deleting ? HOLD_DELAY_MS : TYPE_DELAY_MS);
        return;
      }

      clearTimeout(this.timer);
      this.timer = null;
      if (reducedMotion.matches) this.index = 0;
      // Show the whole text while paused, so a focused input never has half a word; on resume it
      // holds, then deletes, like a text that just finished typing.
      this.length = this.texts[this.index].length;
      this.deleting = true;
      this.render();
    }

    schedule(delay) {
      this.timer = setTimeout(() => this.tick(), delay);
    }

    tick() {
      if (this.deleting) {
        this.length -= 1;
        if (this.length === 0) {
          this.deleting = false;
          this.index = (this.index + 1) % this.texts.length;
        }
      } else {
        this.length += 1;
      }
      this.render();

      let delay = this.deleting ? DELETE_DELAY_MS : TYPE_DELAY_MS;
      if (!this.deleting && this.length === this.texts[this.index].length) {
        this.deleting = true;
        delay = HOLD_DELAY_MS;
      }
      this.schedule(delay);
    }

    render() {
      this.input.placeholder = this.texts[this.index].slice(0, this.length).join('');
    }
  }

  class NeedHelpDeciding extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.typewriter.start();
        return;
      }
      this.initialized = true;

      this.form = this.querySelector('.nhd__form');
      this.input = this.querySelector('.nhd__input');
      this.sendButton = this.querySelector('.nhd__send');
      this.thread = this.querySelector('[data-nhd-thread]');

      let config = {};
      try {
        config = JSON.parse(this.querySelector('[data-nhd-config]').textContent);
      } catch (error) {
        console.error('[need-help-deciding] Invalid config', error);
      }
      this.product = config.product || null;
      this.strings = config.strings || {};
      this.endpoint = (this.dataset.endpoint || '').trim();
      this.openChatAfterSend = this.dataset.openChat === 'true';

      this.history = [];
      this.conversationId = null;
      this.productContextSent = false;
      this.busy = false;
      this.errorMessage = null;

      this.form.addEventListener('submit', this.onSubmit.bind(this));

      let placeholders = [];
      try {
        placeholders = JSON.parse(this.input.dataset.placeholders || '[]');
      } catch (error) {
        console.error('[need-help-deciding] Invalid placeholders', error);
      }
      this.typewriter = new PlaceholderTypewriter(
        this.input,
        placeholders.filter((text) => typeof text === 'string' && text.trim())
      );
      this.typewriter.start();
    }

    disconnectedCallback() {
      this.typewriter?.stop();
    }

    async onSubmit(event) {
      event.preventDefault();
      const question = this.input.value.trim();
      if (!question || this.busy) return;

      // this.errorMessage?.remove();
      // this.errorMessage = null;
      // this.setBusy(true);
      // this.input.value = '';
      // // Clearing the value in code fires no input event; resume if the input isn't focused.
      // this.typewriter.update();

      // const questionMessage = this.addMessage('user', question);
      // const thinkingMessage = this.addMessage('assistant', this.strings.thinking);
      // thinkingMessage.classList.add('nhd__msg--thinking');

      // try {
      //   if (this.endpoint) {
      //     const answer = await this.askEndpoint(question);
      //     thinkingMessage.remove();
      //     this.addMessage('assistant', answer);
      //     this.history.push({ role: 'user', content: question }, { role: 'assistant', content: answer });
      //   } else {
      //     const result = await this.askWidget(question);
      //     thinkingMessage.remove();
      //     this.showWidgetResult(result);
      //   }
      // } catch (error) {
      //   console.error('[need-help-deciding]', error);
      //   thinkingMessage.remove();
      //   questionMessage.remove();
      //   this.errorMessage = this.addMessage('error', this.strings.error);
      //   // Put the question back so the customer can retry without retyping it.
      //   this.input.value = question;
      //   this.input.focus();
      // } finally {
      //   this.setBusy(false);
      // }
      await window.sagepilotWidgetReady;
window.ChatWidget.open();

const result = await window.ChatWidget.sendMessage(
  question
);

if (result.status === "sent") {
  // The message was sent. result.conversationId identifies the conversation.
} else if (result.status === "requires_input") {
  // The chat is showing its contact form or verification step.
} else if (result.status === "unknown") {
  // The message may have been sent. Check the conversation before trying again.
} else {
  // Not sent. result.code explains why.
}
    }

    async askEndpoint(question) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            question,
            conversation_id: this.conversationId,
            history: this.history.slice(-10),
            product: this.product,
            page_url: window.location.href,
          }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Answer API responded ${response.status}`);

        const data = await response.json();
        const answer = [data.answer, data.reply, data.message, data.text]
          .find((value) => typeof value === 'string' && value.trim());
        if (!answer) throw new Error('Answer API returned no answer text');

        if (data.conversation_id) this.conversationId = data.conversation_id;
        return answer.trim();
      } finally {
        clearTimeout(timer);
      }
    }

    async askWidget(question) {
      const widget = await this.waitForWidget();
      // Product context goes with the first question only; follow-ups continue the same chat.
      const text = this.productContextSent ? question : this.withProductContext(question);

      let result = await widget.sendMessage(text);
      for (let attempt = 1; attempt <= WIDGET_MAX_RETRIES && WIDGET_RETRY_CODES.includes(result?.code); attempt++) {
        await wait(1000 * attempt);
        result = await widget.sendMessage(text);
      }

      if (!result || !['sent', 'requires_input', 'unknown'].includes(result.status)) {
        throw new Error(`SagePilot widget: ${result?.code || result?.status || 'no result'}`);
      }
      if (result.status === 'sent') this.productContextSent = true;
      return result;
    }

    async waitForWidget() {
      const deadline = Date.now() + WIDGET_WAIT_MS;
      while (!window.ChatWidget?.sendMessage) {
        if (Date.now() > deadline) throw new Error('SagePilot widget is not available');
        await wait(250);
      }
      return window.ChatWidget;
    }

    withProductContext(question) {
      if (!this.product) return question;
      const lines = [question, '', `Product: ${this.product.title} (ID: ${this.product.id})`];
      if (this.product.description) {
        lines.push(`Description: ${truncate(this.product.description, WIDGET_DESCRIPTION_LIMIT)}`);
      }
      return lines.join('\n');
    }

    showWidgetResult(result) {
      const notes = {
        sent: this.strings.sentToChat,
        requires_input: this.strings.chatNeedsInput,
        unknown: this.strings.unconfirmed,
      };
      const message = this.addMessage('assistant', notes[result.status]);

      const openButton = document.createElement('button');
      openButton.type = 'button';
      openButton.className = 'nhd__chat-link';
      openButton.textContent = this.strings.openChat;
      openButton.addEventListener('click', () => window.ChatWidget?.open());
      message.appendChild(openButton);

      // The customer has to act in the chat window for these, so always open it.
      if (this.openChatAfterSend || result.status !== 'sent') window.ChatWidget.open();
    }

    addMessage(role, text) {
      const message = document.createElement('div');
      message.className = `nhd__msg nhd__msg--${role}`;
      message.textContent = text;
      this.thread.hidden = false;
      this.thread.appendChild(message);
      this.thread.scrollTop = this.thread.scrollHeight;
      return message;
    }

    setBusy(busy) {
      this.busy = busy;
      this.sendButton.disabled = busy;
      this.thread.setAttribute('aria-busy', String(busy));
    }
  }

  customElements.define('need-help-deciding', NeedHelpDeciding);

  // Theme editor: editing a section re-renders its markup. Stop the old typewriters so no timer
  // outlives them, and restart the new ones once they're in the page.
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('need-help-deciding').forEach((element) => element.typewriter?.stop());
  });
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('need-help-deciding').forEach((element) => element.typewriter?.start());
  });
})();
